import { useMutation } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { ErrorMessage, PageLoader, Spinner } from '@/components/ui'
import { api } from '@/lib/api'
import type { MeResponse } from '@/lib/types'
import { useAuthStore } from '@/stores/auth'

/**
 * Принятие приглашения по ссылке из письма.
 *
 * Приглашение адресное: принять его может только тот email, которому оно
 * отправлено. Поэтому неавторизованному пользователю показываем вход и
 * регистрацию, а не пытаемся принять приглашение анонимно.
 */
export function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()

  const isRestoring = useAuthStore((state) => state.isRestoring)
  const isAuthenticated = useAuthStore((state) => state.accessToken !== null && state.user !== null)
  const setSession = useAuthStore((state) => state.setSession)
  const setActiveWorkspace = useAuthStore((state) => state.setActiveWorkspace)

  const accept = useMutation({
    mutationFn: async () => {
      const result = await api.post<{
        workspace_id: string
        workspace_name: string
        role: string
      }>('/invitations/accept', { token }, { skipWorkspace: true })

      // Список пространств изменился — перечитываем профиль
      const me = await api.get<MeResponse>('/auth/me', { skipWorkspace: true })
      return { result, me }
    },
    onSuccess: ({ result, me }) => {
      setSession(me.user, me.workspaces)
      setActiveWorkspace(result.workspace_id)
      void navigate('/', { replace: true })
    },
  })

  if (isRestoring) return <PageLoader label="Проверяем сессию…" />

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <span className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-600 text-base font-bold text-white">
            RF
          </span>
          <h1 className="text-2xl font-semibold text-slate-900">Приглашение в ReportFlow</h1>
        </div>

        <div className="card space-y-4 p-6">
          {isAuthenticated ? (
            <>
              <p className="text-sm text-slate-600">
                Вы приглашены в рабочее пространство. Приглашение действует только для того email,
                на который оно было отправлено.
              </p>

              <ErrorMessage error={accept.error} />

              <button
                type="button"
                className="btn-primary w-full"
                disabled={accept.isPending}
                onClick={() => accept.mutate()}
              >
                {accept.isPending && <Spinner className="h-4 w-4" />}
                Принять приглашение
              </button>

              <Link to="/" className="btn-secondary w-full">
                Позже
              </Link>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-600">
                Чтобы принять приглашение, войдите под тем email, на который оно отправлено, или
                создайте аккаунт с этим адресом.
              </p>
              <Link to="/login" state={{ from: `/invite/${token}` }} className="btn-primary w-full">
                Войти
              </Link>
              <Link to="/register" className="btn-secondary w-full">
                Создать аккаунт
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
